import React, {useCallback, useMemo, useRef, useState} from 'react';
import { connect } from 'react-redux';
import { withStyles, Tooltip, IconButton, Typography, CircularProgress } from '@material-ui/core';
import { hasPermission, hasAtLeastOnePermission, createHistory, formatDate } from '../../sc-utilities';
import { Redirect } from 'react-router-dom';
import { CloudDownload, FindInPage } from '@material-ui/icons';
import TrialVoucherList from './TrialVoucherList';
import { CustomChip } from '../../sc-components';
import {download} from '../../sc-utilities/download.utils';
import { snackActions } from '../../sc-utilities/SnackBar';
import config from "../../../config/config";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='chipActive']": {
            color: theme.palette.success.dark
        },
        "[class*='chipUsed']": {
            color: theme.palette.warning.dark,
        },
        "[class*='chipInactive']": {
            color: theme.palette.error.dark
        },
        "[class*='chipActivationInProgress']": {
            color: theme.palette.info.dark,
        }
    },
    chipActive: {
        color: theme.palette.success.dark
    },
    chipUsed: {
        color: theme.palette.warning.dark,
    },
    chipInactive: {
        color: theme.palette.error.dark
    },
    chipActivationInProgress: {
        color: theme.palette.info.dark,
    }
});

const TrialVoucher = props => {
    const tableRef = useRef();
    const { activeCustomerID, classes, columns, translations, permissions } = props;

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, trialVoucherID: null, redirectPath: '' });
    const [downloading, setDownloading] = useState({});

    let visible = columns && columns['TrialVoucher'];

    const getFormattedStatus = useCallback((id, status) => {
        switch (status) {
            case 'active':
                return <CustomChip key={`${id}`} className={classes['chipActive']} value={translations['active'] || 'Active'} />;
            case 'inactive':
                return <CustomChip key={`${id}`} className={classes['chipInactive']} value={translations['inactive'] || 'Inactive'} />;
            case 'used':
                return <CustomChip key={`${id}`} className={classes['chipUsed']} value={translations['used'] || 'Used'} />;
            case 'activationInProgress':
                return <CustomChip key={`${id}`} className={classes['chipActivationInProgress']} value={translations['activation_in_progress'] || 'Activation in progress'} />;
            default:
                return '';
        }
    }, [translations, classes]);

    const handleDownload = useCallback(async (trialVoucherID) => {
        setDownloading({ [trialVoucherID]: true });
        const objectURL = `${config.backend_domain}:${config.backend_port}/trial-vouchers/download?TrialVoucherID=${trialVoucherID}&ActiveCustomerID=${activeCustomerID}`;
        let exportDataSuccessful = await download(objectURL);
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
        }
    }, [activeCustomerID, translations]);

    const handleBulkDownload = useCallback(async (trialVoucherIDs) => {
        const objectURL = `${config.backend_domain}:${config.backend_port}/trial-vouchers/bulk_download?TrialVoucherIDs=${trialVoucherIDs}&ActiveCustomerID=${activeCustomerID}`;
        let exportDataSuccessful = await download(objectURL);
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
        }
    }, [activeCustomerID, translations]);

    const tableColumns = useMemo(() => {
        return [{
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: rowData => <>
                {hasPermission(permissions, 'trial_voucher_load') ?
                    <Tooltip key={`Edit-${rowData.TrialVoucherID}`} title={translations["trial_voucher_details"] || "Trial voucher details"}>
                        <IconButton
                            id={`edit_trial_voucher_${rowData.TrialVoucherID}`}
                            component='div'
                            onClick={(event) => redirectHandler(`/billing/trial-vouchers/edit?trialVoucherID=${rowData.TrialVoucherID}`)}
                        >
                            <FindInPage />
                        </IconButton>
                    </Tooltip>
                    : null
                }
                {hasPermission(permissions, 'trial_voucher_download') ?
                    <Tooltip title={downloading[rowData.TrialVoucherID] ? translations["loading"] || "Loading.." : translations["download_trial_voucher"] || "Download trial voucher"}>
                        <IconButton component="div" aria-label={downloading[rowData.TrialVoucherID] ? translations["loading"] || "Loading.." : translations["download_trial_voucher"] || "Download trial voucher"}
                            onClick={() => {
                                if (downloading[rowData.TrialVoucherID]) return null; else return handleDownload(rowData.TrialVoucherID)
                            }}>
                            {downloading[rowData.TrialVoucherID] ? <CircularProgress size={20} /> : <CloudDownload />}
                        </IconButton>
                    </Tooltip> : null
                }
            </>
        },
        {
            title: translations["expiry_date"] || 'Expiry Date', field: 'ExpiryDate',
            hidden: visible ? visible['ExpiryDate'] : false,
            type: 'date',
            cellStyle: { minWidth: 150, maxWidth: 150 },
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ExpiryDate ? formatDate(rowData.ExpiryDate) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["voucher_code"] || 'Voucher Code', field: 'VoucherCode',
            hidden: visible ? visible['VoucherCode'] : false,
            filtering: true,
            cellStyle: { minWidth: 200, maxWidth: 200 },
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.VoucherCode ? rowData.VoucherCode : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["credits"] || 'Credits', field: 'Credits',
            hidden: visible ? visible['Credits'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Credits ? rowData.Credits : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["status"] || 'Status', field: 'Status',
            hidden: visible ? visible['Status'] : false,
            lookup: { 'active': translations['active'] || 'Active', 'inactive': translations['inactive'] || 'Inactive', 'used': translations['used'] || 'Used', 'activation_in_progress': translations['activation_in_progress'] || 'Activation in progress' },
            defaultFilter: ['active', 'inactive', 'used', 'activation_in_progress'],
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Status ? getFormattedStatus(rowData.PlanID, rowData.Status) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["number_of_downloads"] || 'Number Of downloads', field: 'NumberOfDownloads',
            hidden: visible ? visible['NumberOfDownloads'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.NumberOfDownloads ? rowData.NumberOfDownloads : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        ];
    }, [translations, visible, handleDownload, downloading, getFormattedStatus, permissions]);

    if (!hasAtLeastOnePermission(permissions, [
        "trial_voucher_load"
    ])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, trialVoucherID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, trialVoucherID: trialVoucherID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return <>
        { hasPermission(permissions, 'trial_voucher_list') ?
            < div className={classes.root} >
                <TrialVoucherList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    handleBulkDownload={handleBulkDownload}
                    tableRef={tableRef}
                />
            </div > : null}
    </>
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(TrialVoucher));